import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { SEOHelmet } from 'components/SEO';
import TextInputField from 'components/TextInputField/TextInputField';
import type { AppDispatch } from 'store';
import { useUpdateUsernameMutation } from 'store/services/auth/authApi';
import { meApi, useGetMeQuery } from 'store/services/user/meApi';
import { useLazyGetUsernameAvailabilityQuery } from 'store/services/play/playApi';
import { validationPatterns } from 'utils/inputSanitization';
import { safePlayReturnTo } from 'utils/play/discordAuth';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { PlayLayout } from '../../layout/PlayLayout';
import '../Invite/InvitePage.css';

type Availability = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

const readMessage = (err: unknown): string | null => {
  const data = (err as { data?: { message?: unknown } } | null)?.data;
  return data && typeof data.message === 'string' ? data.message : null;
};

/**
 * Host name step (Eric's ruling 32, 2026-10-30): a new account that signed up
 * from /play outside an invite (email or Discord) sets its username here and
 * nothing else, then returns to the /play page it started from. First and
 * last name belong to the player name step, which joins an event. Same
 * username rule and availability probe as that step; the save goes through
 * PATCH /users/username (409 taken is the backstop).
 */
const PlayNameStepPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const returnTo = safePlayReturnTo(params.get('returnTo'), '/play');
  const { data: me } = useGetMeQuery();
  const [updateUsername, { isLoading: saving }] = useUpdateUsernameMutation();
  const [checkAvailability] = useLazyGetUsernameAvailabilityQuery();

  const [username, setUsername] = useState('');
  const [availability, setAvailability] = useState<Availability>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const seeded = useRef(false);
  const checkTimer = useRef<number | null>(null);

  useEffect(() => {
    if (seeded.current || !me) return;
    seeded.current = true;
    setUsername(me.username ?? '');
  }, [me]);

  const trimmed = username.trim();
  const isOwnName = !!me && trimmed.toLowerCase() === (me.username ?? '').toLowerCase();
  const matchesRule = validationPatterns.username.test(trimmed);

  useEffect(() => {
    if (checkTimer.current) window.clearTimeout(checkTimer.current);
    if (!trimmed) {
      setAvailability('idle');
      return;
    }
    if (!matchesRule) {
      setAvailability('invalid');
      return;
    }
    if (isOwnName) {
      setAvailability('available');
      return;
    }
    setAvailability('checking');
    checkTimer.current = window.setTimeout(() => {
      checkAvailability(trimmed)
        .unwrap()
        .then((res) => setAvailability(res.available ? 'available' : 'taken'))
        .catch(() => setAvailability('available'));
    }, 350);
    return () => {
      if (checkTimer.current) window.clearTimeout(checkTimer.current);
    };
  }, [trimmed, matchesRule, isOwnName, checkAvailability]);

  const canSubmit = useMemo(() => matchesRule && availability !== 'taken' && availability !== 'checking' && !saving, [matchesRule, availability, saving]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      if (!isOwnName) {
        await updateUsername({ username: trimmed }).unwrap();
        dispatch(meApi.util.invalidateTags(['User']));
      }
      navigate(returnTo, { replace: true });
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 409) setAvailability('taken');
      if (status === 400) setAvailability('invalid');
      setSubmitError(readMessage(err) ?? "Couldn't save that username. Please try again.");
    }
  };

  const statusLine = (() => {
    switch (availability) {
      case 'checking':
        return <p className="play-field__status">Checking…</p>;
      case 'available':
        return <p className="play-field__status play-field__status--ok">Available</p>;
      case 'taken':
        return <p className="play-field__status play-field__status--bad">That username is taken</p>;
      case 'invalid':
        return <p className="play-field__status play-field__status--bad">3 to 20 letters, numbers, dashes or underscores</p>;
      default:
        return null;
    }
  })();

  return (
    <PlayLayout title="TradeMagic Play" backTo={returnTo}>
      <SEOHelmet title="Your username - TradeMagic Play" description="Set your username for TradeMagic Play." noindex />
      <section className="play-card play-name-step" data-testid="play-host-name-step">
        <p className="play-eyebrow">One last step</p>
        <h1 className="play-title">Your username</h1>
        <p className="play-helper">Shown on your events and to the players who join them. Change it anytime from your profile.</p>

        <div className="play-field" style={{ marginTop: 18 }}>
          <label className="play-field__label" htmlFor="play-host-username">
            Username
          </label>
          <TextInputField value={username} placeholder="Username" onChange={setUsername} error={availability === 'taken' || availability === 'invalid'} />
          {statusLine}
        </div>

        <div className="play-name-step__cta">
          <PlayPrimaryButton label="Continue" disabled={!canSubmit} loading={saving} onClick={submit} />
        </div>
        {submitError && (
          <p className="play-error" role="alert">
            {submitError}
          </p>
        )}
      </section>
    </PlayLayout>
  );
};

export default PlayNameStepPage;
